import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InventoryQuickbooksAccountMappingFlatTeamSettingGroupId =
  | "inventory_quickbooks_account_mapping.account_mapping"
  | "inventory_quickbooks_account_mapping.location_mapping";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.ims",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Mapping between Redo inventory activity and the team's QuickBooks accounts and locations.",
};

export const InventoryQuickbooksAccountMappingFlatTeamSettingGroups = [
  {
    id: "inventory_quickbooks_account_mapping.account_mapping",
    searchLabels: [
      "Inventory Asset",
      "Accounts Payable",
      "Payment Account",
      "Freight Expense",
      "Duty Expense",
      "Income",
      "Shrinkage / Adjustment",
    ],
    title: "Account mapping",
    description:
      "Which QuickBooks accounts Redo posts to for inventory, adjustments, and purchase-order bills. An unmapped account stops the matching entries from syncing.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/quickbooks/account-mapping",
    },
    synonyms: ["account mapping", "inventory quickbooks account mapping"],
  },
  {
    id: "inventory_quickbooks_account_mapping.location_mapping",
    searchLabels: [
      "QuickBooks warehouse location",
      "No QB location (optional)",
    ],
    title: "Location mapping",
    description:
      "Pairs each Redo location with its QuickBooks Online counterpart so inventory is tracked per location in QuickBooks.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: {
      type: "settings",
      path: "inventory-management/quickbooks/account-mapping",
    },
    synonyms: ["location mapping", "inventory quickbooks account mapping"],
  },
] as const satisfies readonly SettingGroupDefinition<InventoryQuickbooksAccountMappingFlatTeamSettingGroupId>[];
